import { stockFor } from './beoGen.js';
import { PLACES, SHORTFALLS, placeFor } from '../data/resources.js';

/**
 * What banquet storage actually has on the shelf.
 *
 * Until now the shelves were infinite, which was fine while the only question
 * was whether you could get across the property in time. A goods shortfall is
 * the first thing in the game that can make a room impossible to set, and it
 * only means anything if the shelf can run out.
 *
 * Stock is tied to the accepted event, not to the day. Accepting is the moment
 * the department pulls your par for the room, which is what actually happens,
 * and it means a shortfall you resolved stays resolved without a separate
 * ledger of what you have already drawn.
 *
 * With nothing accepted the shelves are open. You are tidying, not setting.
 */
export function createStores() {
  let stock = null;
  /**
   * What is waiting to be collected, and where.
   *
   * Keyed by the place a shortfall names — a housekeeping cart, a pallet on the
   * dock, a riser stack in the shop. Resolving a shortfall does not put
   * anything on this shelf; it puts it here, at the place its own text has
   * always said it was, and you go and get it.
   */
  let waiting = {};
  const listeners = new Set();

  function emit() {
    for (const listener of listeners) listener(stock);
  }

  return {
    onChange(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    get open() {
      return stock === null;
    },
    get stock() {
      return stock;
    },
    /**
     * Every pile currently waiting somewhere, for the save and for the HUD.
     *
     * A copy, piles and all. Handing out the live object meant a script that
     * asked what was on the cart, collected it, and then asked again got the
     * same object back twice with the second answer in it.
     */
    get waiting() {
      return Object.fromEntries(
        Object.entries(waiting).map(([place, pile]) => [place, { ...pile }])
      );
    },

    /** Pulls the par for an event, minus whatever it is short of. */
    openFor(beo) {
      stock = beo ? stockFor(beo) : null;
      // A new room does not inherit the last room's deliveries. What is on the
      // pallet was released against an event, and that event is over.
      waiting = {};
      emit();
    },

    close() {
      stock = null;
      waiting = {};
      emit();
    },

    /** How many are on the shelf. Infinity when nothing is accepted. */
    available(item) {
      if (!stock) return Infinity;
      return stock[item] ?? 0;
    },

    /** Draws up to `want`. Returns how many there actually were. */
    draw(item, want) {
      if (!stock) return want;
      const got = Math.min(want, stock[item] ?? 0);
      if (got > 0) {
        stock[item] -= got;
        emit();
      }
      return got;
    },

    /** Puts stock back — a struck round returns to the shelf. */
    give(item, count) {
      if (!stock || count <= 0) return;
      stock[item] = (stock[item] ?? 0) + count;
      emit();
    },

    /**
     * Lets go of what a resolved shortfall was holding, wherever the department
     * feels like leaving it.
     *
     * It used to land in banquet storage whatever the sheet said, which made
     * every shortfall the same errand with a different name on it. Then it
     * landed where the sheet said, which made each one a different walk. This
     * is the third thing: *which* of its places, read off the rung you are on
     * with the department that was holding it.
     *
     * Cold leaves it where it sat. Warm has walked it half way. Trusted has it
     * waiting where you were going anyway, which is `arrived` — goods go onto
     * the shelf and come off it with the rest of the par, and staging is
     * already in the room. Nothing is piled at an arrived place, because there
     * is nothing there to go and get.
     *
     * Returns what happened rather than a count, because the caller has a line
     * to say and three different things to say in it.
     */
    release(entry, rung = 'cold') {
      const shortfall = SHORTFALLS[entry.id];
      const nothing = { count: 0, at: null, arrived: false };
      if (!shortfall?.item || !shortfall.at) return nothing;
      const at = placeFor(shortfall, rung);
      if (!at) return nothing;
      // Goods come in the quantity that was withheld; a staging line is one
      // thing, because there is one board and one stack of risers.
      const count = shortfall.kind === 'goods' ? entry.amount : 1;

      if (PLACES[at]?.arrived) {
        // Straight into the par for goods. Staging has nothing to add to and is
        // the caller's to mark, because what a staging line means is a rule
        // about the room rather than about the shelf.
        if (shortfall.kind === 'goods' && stock) {
          stock[shortfall.item] = (stock[shortfall.item] ?? 0) + count;
        }
        emit();
        return { count, at, arrived: true, item: shortfall.item };
      }

      const pile = waiting[at];
      waiting[at] = {
        item: shortfall.item,
        count: (pile?.item === shortfall.item ? pile.count : 0) + count,
      };
      emit();
      return { count, at, arrived: false, item: shortfall.item };
    },

    /** What is on a given spot, or null. */
    waitingAt(place) {
      const pile = waiting[place];
      return pile && pile.count > 0 ? { ...pile } : null;
    },

    /** Takes up to `want` off a spot. Returns how many were actually there. */
    collect(place, want) {
      const pile = waiting[place];
      if (!pile || pile.count <= 0) return 0;
      const got = Math.min(want, pile.count);
      pile.count -= got;
      if (pile.count <= 0) delete waiting[place];
      emit();
      return got;
    },

    toJSON: () => ({
      stock: stock ? { ...stock } : null,
      waiting: Object.fromEntries(
        Object.entries(waiting).map(([place, pile]) => [place, { ...pile }])
      ),
    }),
    load(state) {
      /*
       * Saves written before deliveries existed are the stock object itself
       * rather than a wrapper around it. Reading both shapes costs one line and
       * means a save from yesterday still loads.
       */
      const shaped = state && Object.hasOwn(state, 'stock') ? state : { stock: state, waiting: {} };
      stock = shaped.stock ? { ...shaped.stock } : null;
      waiting = { ...(shaped.waiting ?? {}) };
      emit();
    },
  };
}
